import type { ReactNode } from 'react';
import { ScreenLink as Link } from '../permissions/ScreenLink';
import { ChevronDown } from 'lucide-react';
import { TONE_TEXT, type Tone } from './tone';
import type { PeekTriggerProps } from './PeekDropdown';

interface KpiProps {
  label: string;
  value: ReactNode;
  /** Context under the value, e.g. "of 120 budgeted". */
  hint?: ReactNode;
  /** Optional semantic tone for the value (use only when the number itself is a warning/success signal). */
  tone?: Tone;
  icon?: ReactNode;
  to?: string;
  /**
   * Opens a peek instead of navigating (`usePeek().triggerProps(key)`): the
   * card becomes a `button` with `aria-haspopup="dialog"` / `aria-expanded`.
   * Wins over `to`.
   */
  peek?: PeekTriggerProps;
  className?: string;
}

/**
 * One KPI (DESIGN_SYSTEM.md §9): quiet white surface, label → large tabular
 * value → context. No per-card accent colours.
 */
export default function Kpi({ label, value, hint, tone, icon, to, peek, className = '' }: KpiProps) {
  // Spans (display: block), so the same body is valid inside a button.
  const body = (
    <>
      <span className="flex items-start justify-between gap-3">
        <span className="block text-sm text-surface-600">
          {label}
          {peek && (
            <ChevronDown
              size={14}
              className="-mt-0.5 ms-1 inline-block align-middle text-text-3 transition duration-micro group-hover:text-text-2 group-aria-expanded:rotate-180"
              aria-hidden="true"
            />
          )}
        </span>
        {icon && <span className="text-surface-400 flex-shrink-0" aria-hidden="true">{icon}</span>}
      </span>
      <span className={`block mt-1 text-2xl sm:text-3xl font-semibold tracking-tight tabular-nums ${tone ? TONE_TEXT[tone] : 'text-surface-900'}`}>
        {value}
      </span>
      {hint && <span className="block mt-1 text-xs text-surface-500">{hint}</span>}
    </>
  );
  const cls = `block bg-surface-0 border border-surface-200 rounded-xl p-5 ${className}`;
  if (peek) {
    return (
      <button
        {...peek}
        className={`group ${cls} w-full text-start hover:border-surface-300 hover:shadow-card aria-expanded:border-surface-300 aria-expanded:shadow-card transition-[border-color,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus`}
      >
        {body}
      </button>
    );
  }
  return to ? (
    <Link to={to} className={`${cls} hover:border-surface-300 hover:shadow-card transition-[border-color,box-shadow] duration-150`}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}
